import { FilterDefs, useSessionStorage } from "@homebound/beam";
import { useEffect, useMemo, useRef } from "react";
import { safeKeys } from "src/utils";
import { Temporal } from "temporal-polyfill";
import { JsonParam, useQueryParams } from "use-query-params";

type PersistedFilterHook<F> = { filter: F; setFilter: (filter: F) => void };

type UsePersistedFilterProps<F> = { filterDefs: FilterDefs<F>; storageKey: string };

// Build a custom persisted filter to be able to create a unqiue query param key
// This allows us to avoid using the query param key: "filter" (beam's default param key name when usePersistedFilter is called)
// And prevents the budgetSupeDrawer activity tab filter, which also has a param key named "filter"
// from overriding the budget page's filter and blowing away the page's filter values by uniquing the keys

// TODO: create a persisted filter for beam that allows dynamic creation of param key names
export function useStorageFilter<F>({ storageKey, filterDefs }: UsePersistedFilterProps<F>): PersistedFilterHook<F> {
  const filterKeys = Object.keys(filterDefs);
  const defaultFilter = useMemo(
    () =>
      Object.fromEntries(
        safeEntries(filterDefs)
          .filter(([key, def]) => def(key as string).defaultValue !== undefined)
          .map(([key, def]) => [key, def(key as string).defaultValue]),
      ),
    [filterDefs],
  );
  const [params, setQueryParams] = useQueryParams({ [storageKey]: JsonParam });
  const queryParams = params[storageKey];
  const [storedFilter, setStoredFilter] = useSessionStorage<F>(storageKey, queryParams ?? defaultFilter);
  const isQueryParamFilterValid = hasValidFilterKeys(queryParams, filterKeys);
  const rawFilter = isQueryParamFilterValid ? queryParams : (storedFilter ?? defaultFilter);
  // Temporal rehydration turns persisted ISO dates back into PlainDate instances, which allocates
  // new objects. Cache by the serialized filter so consumers keep a stable reference for unchanged
  // values; otherwise memo/effect chains can retrigger forever (BudgetPage hit this in built Storybook).
  const serializedFilter = JSON.stringify(rawFilter);
  const normalizedFilterRef = useRef<{ key: string | undefined; value: F }>();

  if (normalizedFilterRef.current?.key !== serializedFilter) {
    normalizedFilterRef.current = { key: serializedFilter, value: normalizeTemporalDates(rawFilter) };
  }

  const filter = normalizedFilterRef.current.value;

  const setFilter = (filter: F) => setQueryParams({ [storageKey]: filter });

  useEffect(
    () => {
      if (queryParams === undefined) {
        // if there is no filter in the query params, use stored filter
        // "replaceIn" replaces the url in history instead of creating a new history item
        // back button will go to previous url
        setQueryParams({ [storageKey]: storedFilter }, "replaceIn");
      } else if (!isQueryParamFilterValid) {
        // if there are invalid query params, fallback to the default filters
        setQueryParams({ [storageKey]: defaultFilter }, "replaceIn");
      } else if (JSON.stringify(queryParams) !== JSON.stringify(storedFilter)) {
        // if there is a valid filter in query params and its different from the
        // current storedFilter, use query params filter
        setStoredFilter(queryParams);
      }
    },
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storedFilter, setStoredFilter, setQueryParams, queryParams],
  );

  return { setFilter, filter };
}

type Entries<T> = { [K in keyof T]: [K, T[K]] }[keyof T][];

function safeEntries<T extends object>(obj: T): Entries<T> {
  return Object.entries(obj) as any;
}

// check for valid filter keys in the query params
function hasValidFilterKeys<F>(queryParamsFilter: F, definedKeys: (keyof F)[]): queryParamsFilter is F {
  return queryParamsFilter && safeKeys(queryParamsFilter).every((key) => definedKeys.includes(key as keyof F));
}

function normalizeTemporalDates<T>(value: T): T {
  // Query/session storage round-trips PlainDates as ISO strings, but Beam date filters expect real PlainDate
  // instances when they render mock date inputs. Rehydrate those persisted `YYYY-MM-DD` strings before the
  // filter state is handed back to the UI.
  if (isSerializedPlainDate(value)) {
    return Temporal.PlainDate.from(value) as T;
  } else if (isPlainDateRecord(value)) {
    return Temporal.PlainDate.from(value) as T;
  } else if (Array.isArray(value)) {
    return value.map((item) => normalizeTemporalDates(item)) as T;
  } else if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, normalizeTemporalDates(item)])) as T;
  } else {
    return value;
  }
}

function isSerializedPlainDate(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function isPlainDateRecord(value: unknown): value is Temporal.PlainDateLike {
  return (
    !!value &&
    typeof value === "object" &&
    "year" in value &&
    "month" in value &&
    "day" in value &&
    typeof value.year === "number" &&
    typeof value.month === "number" &&
    typeof value.day === "number"
  );
}
